// Care Workforce API Configuration
// This is a separate serverless backend for managing Australia Care Workforce applications

const CARE_WORKFORCE_API_CONFIG = {
  DEV: {
    BASE_URL: "https://0pwqgu87n5.execute-api.eu-north-1.amazonaws.com/prod",
  },
  PROD: {
    BASE_URL: "https://0pwqgu87n5.execute-api.eu-north-1.amazonaws.com/prod",
  },
};

// Current environment
const CURRENT_ENV = "DEV";

// Base URL for current environment
export const CARE_WORKFORCE_BASE_URL =
  CARE_WORKFORCE_API_CONFIG[CURRENT_ENV].BASE_URL;

// API Endpoints
export const CARE_WORKFORCE_ENDPOINTS = {
  SEND_VERIFICATION_CODE: `${CARE_WORKFORCE_BASE_URL}/send-verification-code`,
  VERIFY_EMAIL: `${CARE_WORKFORCE_BASE_URL}/verify-email`,
  SUBMIT_APPLICATION: `${CARE_WORKFORCE_BASE_URL}/care-workforce-application`,
  GET_APPLICATIONS: `${CARE_WORKFORCE_BASE_URL}/care-workforce-applications`,
  DELETE_APPLICATIONS: `${CARE_WORKFORCE_BASE_URL}/care-workforce-applications`,
};

// Default headers
const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

// API utility functions
export const careWorkforceApiUtils = {
  // Handle API response
  handleResponse: async (response) => {
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: "Unknown error" }));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }
    return response.json();
  },

  // Make API request
  makeRequest: async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        headers: DEFAULT_HEADERS,
        ...options,
      });
      return await careWorkforceApiUtils.handleResponse(response);
    } catch (error) {
      console.error("Care Workforce API Request Error:", error);
      throw error;
    }
  },

  // Build query string from parameters
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        searchParams.append(key, params[key]);
      }
    });
    return searchParams.toString();
  },

  // Export data to CSV
  exportToCSV: (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || "";
            return typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};

// Service functions
export const careWorkforceService = {
  // Send verification code to email
  sendVerificationCode: async ({ email, fullName }) => {
    return careWorkforceApiUtils.makeRequest(
      CARE_WORKFORCE_ENDPOINTS.SEND_VERIFICATION_CODE,
      {
        method: "POST",
        body: JSON.stringify({ email, fullName }),
      },
    );
  },

  // Verify email with OTP
  verifyEmail: async ({ email, verificationCode }) => {
    return careWorkforceApiUtils.makeRequest(
      CARE_WORKFORCE_ENDPOINTS.VERIFY_EMAIL,
      {
        method: "POST",
        body: JSON.stringify({ email, verificationCode }),
      },
    );
  },

  // Submit application (requires verificationId)
  submitApplication: async (applicationData) => {
    return careWorkforceApiUtils.makeRequest(
      CARE_WORKFORCE_ENDPOINTS.SUBMIT_APPLICATION,
      {
        method: "POST",
        body: JSON.stringify(applicationData),
      },
    );
  },

  // Get applications with filtering
  getApplications: async (options = {}) => {
    const params = {
      page: options.page || 1,
      limit: options.limit || 500,
      ...(options.email && { email: options.email }),
      ...(options.startDate && { startDate: options.startDate }),
      ...(options.endDate && { endDate: options.endDate }),
    };

    const queryString = careWorkforceApiUtils.buildQueryString(params);
    const url = `${CARE_WORKFORCE_ENDPOINTS.GET_APPLICATIONS}?${queryString}`;

    return careWorkforceApiUtils.makeRequest(url, {
      method: "GET",
    });
  },

  // Delete applications
  deleteApplications: async (applicationIds) => {
    return careWorkforceApiUtils.makeRequest(
      CARE_WORKFORCE_ENDPOINTS.DELETE_APPLICATIONS,
      {
        method: "DELETE",
        body: JSON.stringify({ applicationIds }),
      },
    );
  },
};

export default careWorkforceService;
